import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  Optional,
  Self
} from '@angular/core';
import { NgControl } from '@angular/forms';

declare var $: any;
declare var alertify: any;

@Directive({
  selector: '[datepickerManualAndCalSelection]',
  standalone: true
})
export class DatepickerManualAndCalSelectionDirective implements AfterViewInit {

  @Input() isDob: boolean = false;

  private lastValue = '';
  private dashTypedManually = false;
  private isDeleting = false;
  private previousMonth: number | null = null;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Optional() @Self() private ngControl: NgControl
  ) { }


  ngAfterViewInit(): void {
    const input = this.el.nativeElement;
    const elem = $(input);
    const control = this.ngControl?.control;

    //New
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('autocomplete', 'off');
    //New

    elem.datepicker({
      dateFormat: 'mm-dd-yy',
      yearRange: this.isDob ? '1900:+0' : '1900:+10',
      maxDate: this.isDob ? 0 : null,
      changeYear: true,
      changeMonth: true,
      showOn: 'none',
      beforeShow: (input: any, inst: any) => {
        setTimeout(() => {
          const dp = $('#ui-datepicker-div');
          $(input).closest('.position-relative').append(dp);

          dp.css({
            position: 'absolute',
            top: '100%',
            left: '0',
            zIndex: 9999
          });
        });
      },
      onSelect: (dateText: string) => {

        this.previousMonth = parseInt(dateText.slice(0, 2), 10);

        if (control) {
          control.setValue(dateText, { emitEvent: false });
        }

        input.value = dateText;
        this.lastValue = dateText;
      }
    });

    elem.parent().find('.icon__20--').on('click', () => {
      elem.datepicker('show');
    });
  }

  //New
  private get isMobile(): boolean {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }
  //New

  private isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }

  private getMaxDays(month: number, year?: number | null): number {
    if (month === 2) {
      if (!year) return 29;
      return year && this.isLeapYear(year) ? 29 : 28;
    }
    return [4, 6, 9, 11].includes(month) ? 30 : 31;
  }

  private resetDateFlow(): void {
    this.lastValue = '';
    this.previousMonth = null;
    this.dashTypedManually = false;
    this.isDeleting = false;
  }

  private isValidMonth(mm: string): boolean {
    return mm.length === 2 && +mm >= 1 && +mm <= 12;
  }

  private getRawMonth(value: string): string {
    const parts = value.split('-');
    return parts[0] || '';
  }

  private isMonthIncomplete(raw: string): boolean {
    const parts = raw.split('-');
    return !parts[0] || parts[0].length < 2;
  }

  private isDayIncomplete(raw: string): boolean {
    const parts = raw.split('-');
    return parts.length >= 2 && (!parts[1] || parts[1].length < 2);
  }

  private setDirectiveError(message: string) {
    const control = this.ngControl?.control;
    if (!control) return;

    const existingErrors = control.errors || {};

    control.setErrors({
      ...existingErrors,
      directiveError: message
    });
  }

  private clearDirectiveError() {
    const control = this.ngControl?.control;
    if (!control || !control.errors) return;

    const updated = { ...control.errors };
    delete updated['directiveError'];

    if (Object.keys(updated).length === 0) {
      control.setErrors(null);
    } else {
      control.setErrors(updated);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {

    const input = this.el.nativeElement;
    const key = e.key;
    const value = input.value || '';
    let dashIndex = -1;
    const caretPos = input.selectionStart || 0;
    const digits = value.replace(/\D/g, '');
    const digitsBeforeCaret =
      value.slice(0, caretPos).replace(/\D/g, '').length;

    if (
      (caretPos === 0 && value[0] === "1" && key >= '2') ||
      (caretPos === 0 && value[0] === "2" && key >= '2')
    ) {
      e.preventDefault();
      this.setDirectiveError("Invalid month. Month must be between 1 and 12");
      return;
    }

    /* DELETE / BACKSPACE */
    if (key === 'Backspace' || key === 'Delete') {
      this.isDeleting = true;

      if (
        value.length >= 2 &&
        value[0] === '0' &&
        value[1] > '2' &&
        value[1] <= '9' &&
        caretPos === 1
      ) {
        e.preventDefault();
        this.setDirectiveError("You are not allowed to remove '0' from month");
        return;
      }

      if (key === 'Backspace' && value[caretPos - 1] === '-') {
        dashIndex = caretPos - 1;
      }

      if (key === 'Delete' && value[caretPos] === '-') {
        dashIndex = caretPos;
      }

      if (dashIndex !== -1) {
        const afterDash = value.slice(dashIndex + 1).replace(/-/g, '');
        if (afterDash.length > 0) {
          e.preventDefault();
          this.setDirectiveError("Invalid date format. ‘-’ is required between month, day, and year.");
          return;
        }
      }
      return;
    }

    if (["ArrowLeft", "ArrowRight", "Tab"].includes(key)) return;

    if (!/^\d$/.test(key) && key !== '-') {
      e.preventDefault();
      return;
    }

    if (digitsBeforeCaret === 0 && key >= '2' && key <= '9') {
      e.preventDefault();
      const newValue =
        value.slice(0, caretPos) + '0' + key + value.slice(caretPos);

      input.value = newValue;
      input.dispatchEvent(new Event('input'));
    }

    if (
      value.indexOf('-') === 2 &&
      digits.slice(0, 2).length === 2 &&
      caretPos <= 2
    ) {
      e.preventDefault();
      this.setDirectiveError("Invalid month. Only two digits are allowed for the month.");
      return;
    }

    if (
      value.lastIndexOf('-') === 5 &&
      digits.slice(2, 4).length === 2 &&
      caretPos > 2 &&
      caretPos <= 5
    ) {
      e.preventDefault();
      this.setDirectiveError("Invalid day. Only two digits are allowed for the day.");
      return;
    }

    if (digitsBeforeCaret === 1 && digits[0] === '0' && key === '0') {
      e.preventDefault();
      this.setDirectiveError("Invalid month format. Leading zero is required for single-digit month.");
      return;
    }

    if (digitsBeforeCaret === 3 && digits[2] === '0' && key === '0') {
      e.preventDefault();
      this.setDirectiveError("Invalid day format. Leading zero is required for single-digit day.");
      return;
    }

    if (key === '-') {
      if (caretPos === 2 || caretPos === 5) {
        this.dashTypedManually = true;
        return;
      }
      e.preventDefault();
      return;
    }

    /* ---------- MONTH ---------- */
    if (digitsBeforeCaret === 1) {
      if (digits[0] === '1' && +key > 2) {
        e.preventDefault();
        this.setDirectiveError("Invalid month. Month must be between 1 and 12");
        return;
      }
    }

    /* ---------- DAY: FIRST DIGIT ---------- */
    if (digitsBeforeCaret === 2) {
      const rawMonth = this.getRawMonth(value);

      if (rawMonth.length !== 2 || !this.isValidMonth(rawMonth)) {
        return;
      }

      const month = parseInt(rawMonth, 10);

      if (key >= '4' && key <= '9') {
        e.preventDefault();
        input.value =
          value.slice(0, caretPos) + '0' + key + value.slice(caretPos);
        input.dispatchEvent(new Event('input'));
        return;
      }

      if (month === 2 && key === '3') {
        e.preventDefault();
        input.value =
          value.slice(0, caretPos) + '03' + value.slice(caretPos);
        input.dispatchEvent(new Event('input'));
        return;
      }
      return;
    }

    if (digitsBeforeCaret === 3) {
      const rawMonth = this.getRawMonth(value);
      const month = parseInt(rawMonth, 10);
      const firstDayDigit = digits[2];
      const secondDigit = key;
      const year =
        digits.length >= 7 ? parseInt(digits.slice(4, 8), 10) : null;

      const dayCandidate = parseInt(firstDayDigit + secondDigit, 10);
      const maxDays = this.getMaxDays(month, year);

      if (rawMonth.length !== 2 || !this.isValidMonth(rawMonth)) {
        return;
      }

      if (dayCandidate > maxDays) {
        e.preventDefault();
        this.setDirectiveError(`Invalid day. This month has only ${maxDays} days.`);
        return;
      }
    }

    if (digitsBeforeCaret === 4 && key !== '1' && key !== '2') {
      e.preventDefault();
      this.setDirectiveError("Invalid year. Year cannot be earlier than 1800.");
      return;
    }

    if (digitsBeforeCaret === 5 && digits[4] === '2' && key !== '0') {
      e.preventDefault();
      this.setDirectiveError("Invalid year. Year cannot be earlier than 1800.");
      return;
    }

    if (digitsBeforeCaret === 5 && digits[4] === '1' && key <= '7') {
      e.preventDefault();
      this.setDirectiveError("Invalid year. Year cannot be earlier than 1800.");
      return;
    }

    if (digitsBeforeCaret === 6 && digits[5] === '0' && +key >= 3) {
      e.preventDefault();
      this.setDirectiveError("Invalid year. Year cannot be earlier than 1800.");
      return;
    }

    if (digitsBeforeCaret >= 4) {
      const mm = digits.slice(0, 2);
      const dd = digits.slice(2, 4);
      const partialYear =
        digits.slice(4, digitsBeforeCaret) + key;

      if (mm === '02' && dd === '29' && partialYear.length >= 1) {
        const yearNum = parseInt(partialYear, 10);

        if (partialYear.length === 4 && !this.isLeapYear(yearNum)) {
          e.preventDefault();
          this.setDirectiveError("The entered year is not a leap year.");
          return;
        }
      }
    }

    const nextDigits =
      digits.slice(0, digitsBeforeCaret) +
      key +
      digits.slice(digitsBeforeCaret);

    if (this.isDob) {
      const today = new Date();
      const mm = +nextDigits.slice(0, 2);
      const dd = +nextDigits.slice(2, 4);
      const yyyy = +nextDigits.slice(4, 8);

      today.setHours(0, 0, 0, 0);

      if (yyyy > today.getFullYear()) {
        e.preventDefault();
        this.setDirectiveError("Future dates are not allowed. Please select a valid date.");
        return;
      }

      if (yyyy === today.getFullYear() && mm) {
        if (mm - 1 > today.getMonth()) {
          e.preventDefault();
          this.setDirectiveError("Future dates are not allowed. Please select a valid date.");
          return;
        }

        if (dd) {
          const d = new Date(yyyy, mm - 1, dd);
          if (d > today) {
            e.preventDefault();
            this.setDirectiveError("Future dates are not allowed. Please select a valid date.");
            return;
          }
        }
      }
    }
    this.clearDirectiveError();
  }

  //New
  @HostListener('beforeinput', ['$event'])
  onBeforeInput(e: InputEvent) {
    if (!this.isMobile) return;

    const inputType = e.inputType;

    if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
      this.isDeleting = true;
      return;
    }

    if (inputType === 'insertText') {
      const data = e.data || '';
      if (!/^[\d-]$/.test(data)) {
        e.preventDefault();
        return;
      }
    }
  }
  //New

  @HostListener('input')
  onInput() {

    const input = this.el.nativeElement;
    //const raw = input.value || '';
    let raw = input.value || ''; //New
    const digits = raw.replace(/\D/g, '');
    const control = this.ngControl?.control;

    if (!control) return;

    //New
    if (this.isMobile) {
      const cleaned = raw.replace(/[^\d-]/g, '');
      if (cleaned !== raw) {
        input.value = cleaned;
        raw = cleaned;
      }
    }
    //New

    if (this.isMonthIncomplete(raw)) {
      control.setValue(raw, { emitEvent: false });
      this.lastValue = raw;
      this.previousMonth = null;
      return;
    }

    if (this.isDayIncomplete(raw)) {
      control.setValue(raw, { emitEvent: false });
      this.lastValue = raw;
      return;
    }

    if (raw.startsWith('-')) {
      const match = raw.match(/^-(\d)$/);
      if (match) {
        const digit = match[1];
        if (+digit >= 2) {
          const fixed = '0' + digit + raw.slice(2);
          control.setValue(fixed, { emitEvent: false });
          input.value = fixed;
          this.lastValue = fixed;
          this.previousMonth = parseInt('0' + digit, 10);
          return;
        }
      }

      control.setValue(raw, { emitEvent: false });
      this.lastValue = raw;
      return;
    }

    if (digits.length === 1 && digits[0] >= '2' && digits[0] <= '9') {
      const fixed = '0' + digits[0];
      input.value = fixed;
      control.setValue(fixed, { emitEvent: false });
      this.lastValue = fixed;
      this.previousMonth = parseInt(fixed, 10);
      this.dashTypedManually = false;
      this.isDeleting = false;
      return;
    }

    //New
    if (this.isMobile) {
      const capped = digits.slice(0, 8);
      if (capped !== digits) {
        input.value = capped;
        raw = capped;
      }
    }
    //New

    if (raw === '') {
      this.resetDateFlow();
      return;
    }

    if (digits.length >= 4) {
      const day = digits.slice(2, 4);
      if (day === '00') {
        control.setValue('', { emitEvent: false });
        input.value = '';
        this.previousMonth = null;
        this.lastValue = '';
        this.setDirectiveError("Invalid day format. Leading zero is required for single-digit day.");
        return;
      }
    }

    if (digits.length >= 2) {
      const month = digits.slice(0, 2);
      if (month === '00') {
        control.setValue('', { emitEvent: false });
        input.value = '';
        this.previousMonth = null;
        this.lastValue = '';
        this.setDirectiveError("Invalid month format. Leading zero is required for single-digit month.");
        return;
      }
    }

    let mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);

    if (this.isValidMonth(mm) && dd.length === 2) {
      const monthNum = parseInt(mm, 10);
      const dayNum = parseInt(dd, 10);
      const yearNum = yyyy.length === 4 ? parseInt(yyyy, 10) : null;

      const maxDays = this.getMaxDays(monthNum, yearNum);

      if (dayNum > maxDays) {
        control.setValue('', { emitEvent: false });
        input.value = '';
        this.previousMonth = null;
        this.lastValue = '';
        this.setDirectiveError(`Invalid date, This month has only ${maxDays} days.`);
        return;
      }
    }

    if (this.isValidMonth(mm) && mm === '02' && dd === '29' && yyyy.length === 4) {
      const yearNum = parseInt(yyyy, 10);
      if (!this.isLeapYear(yearNum)) {
        control.setValue('', { emitEvent: false });
        input.value = '';
        this.previousMonth = null;
        this.lastValue = '';
        this.setDirectiveError("Invalid date. The entered year is not a leap year");
        return;
      }
    }

    if (this.isDeleting) {

      let value = raw;
      const prevDigits = (this.lastValue || '').replace(/\D/g, '');
      const currDigits = digits;

      if (this.isDeleting && prevDigits.length >= 2 && currDigits.length === 1) {
        control.setValue('', { emitEvent: false });
        input.value = '';
        this.previousMonth = null;
        this.lastValue = '';
        return;
      }

      if (value.endsWith('-')) {
        value = value.slice(0, -1);
      }

      control.setValue(value, { emitEvent: false });
      input.value = value;
      this.lastValue = value;
      this.isDeleting = false;
      return;
    }

    const manualDashUsed = this.dashTypedManually;
    this.dashTypedManually = false;

    if (mm.length === 1 && +mm >= 2) mm = '0' + mm;

    if (mm.length === 2 && (+mm < 1 || +mm > 12)) return;

    if (this.isValidMonth(mm)) {
      const newMonth = parseInt(mm, 10);
      const day = dd.length === 2 ? parseInt(dd, 10) : null;
      const year = yyyy.length === 4 ? parseInt(yyyy, 10) : null;

      if (this.previousMonth === null) {
        this.previousMonth = newMonth;
      } else if (this.previousMonth !== newMonth && day !== null) {
        const maxDays = this.getMaxDays(newMonth, year);
        if (day > maxDays) {
          control.setValue('', { emitEvent: false });
          input.value = '';
          this.previousMonth = null;
          this.lastValue = '';
          this.setDirectiveError("Invalid date format. Please enter the date in MM-DD-YYYY format");
          return;
        }
        this.previousMonth = newMonth;
      }
    }

    let formatted;

    if (manualDashUsed) {
      formatted = raw;
    } else {
      formatted = mm;
      if (dd) formatted += '-' + dd;
      if (yyyy) formatted += '-' + yyyy;
    }

    control.setValue(formatted, { emitEvent: false });
    input.value = formatted;
    this.lastValue = formatted;
    this.clearDirectiveError();
  }


  @HostListener('blur')
  onBlur() {

    const input = this.el.nativeElement;
    const value = input.value || '';
    const digits = value.replace(/\D/g, '');
    const control = this.ngControl?.control;

    if (!control) return;

    if (digits.length !== 8 && digits.length !== 0) {

      control.setValue('', { emitEvent: false });
      input.value = '';

      this.previousMonth = null;
      this.lastValue = '';

      this.setDirectiveError("Invalid date format. Please enter the date in MM-DD-YYYY format");
    }
    this.clearDirectiveError();
  }
}